// =============================================================================
// MeuDriver — Batch U4: Hook de templates de resposta rápida no chat
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import type {
  InstructorChatTemplateRow,
  InstructorChatTemplateInsert,
  InstructorChatTemplateUpdate,
} from '@/types/database'

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const templateKeys = {
  all: ['chat-templates'] as const,
  list: (instructorId: string) => [...templateKeys.all, instructorId] as const,
}

// ---------------------------------------------------------------------------
// useInstructorChatTemplates — lista templates do instrutor autenticado
// ---------------------------------------------------------------------------

export function useInstructorChatTemplates(instructorId: string) {
  return useQuery<InstructorChatTemplateRow[]>({
    queryKey: templateKeys.list(instructorId),
    enabled: !!instructorId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('instructor_chat_templates')
        .select('*')
        .eq('instructor_id', instructorId)
        .order('display_order')
        .order('created_at')
      if (error) throw error
      return (data ?? []) as InstructorChatTemplateRow[]
    },
    staleTime: 1000 * 60 * 5,
  })
}

// ---------------------------------------------------------------------------
// useCreateTemplate
// ---------------------------------------------------------------------------

export function useCreateTemplate(instructorId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (values: Pick<InstructorChatTemplateInsert, 'label' | 'body' | 'display_order'>) => {
      const { error } = await supabase
        .from('instructor_chat_templates')
        .insert({ ...values, instructor_id: instructorId })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.list(instructorId) })
      toast.success('Template criado!')
    },
    onError: (err: Error) => {
      toast.error('Erro ao criar template: ' + err.message)
    },
  })
}

// ---------------------------------------------------------------------------
// useUpdateTemplate
// ---------------------------------------------------------------------------

export function useUpdateTemplate(instructorId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...values }: { id: string } & InstructorChatTemplateUpdate) => {
      const { error } = await supabase
        .from('instructor_chat_templates')
        .update(values)
        .eq('id', id)
        .eq('instructor_id', instructorId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.list(instructorId) })
      toast.success('Template atualizado!')
    },
    onError: (err: Error) => {
      toast.error('Erro ao atualizar template: ' + err.message)
    },
  })
}

// ---------------------------------------------------------------------------
// useDeleteTemplate
// ---------------------------------------------------------------------------

export function useDeleteTemplate(instructorId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (templateId: string) => {
      const { error } = await supabase
        .from('instructor_chat_templates')
        .delete()
        .eq('id', templateId)
        .eq('instructor_id', instructorId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.list(instructorId) })
      toast.success('Template removido!')
    },
    onError: (err: Error) => {
      toast.error('Erro ao remover template: ' + err.message)
    },
  })
}
